'use client'

/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * Universal icon component. Renders the self-hosted Framework7 Icons font
 * (plan task 1.6) through the Material Symbols product-name → Framework7
 * glyph mapping in `./iconMap.ts` (plan task 1.7, ported from the retired
 * `Theme/next/iconSet.ts`). `name` is a closed union generated from
 * `./manifest.json` (plan task 1.8, `./names.generated.ts`) — the database
 * `Icon` columns and every call site keep using these Material Symbols
 * product names unchanged; only this rendering layer maps them onto
 * whichever font is currently in fashion.
 *
 * The glyph is drawn from the resolved Framework7 name's text content via
 * the font's own ligature substitution (`font-feature-settings: 'liga'`),
 * the same mechanism the retired Material Symbols rendering used — see
 * `apps/*\/src/app/layout.tsx` and `Theme/tokens.ts`'s `iconFontFamily` for
 * where the font itself is self-hosted and published.
 */

import { Box } from '@mui/material'
import { resolveGlyph } from './iconMap'
import { iconFontFamily, iconSize } from '../Theme/tokens'
import type { TUIIconProps } from './types'

export type {
  TUIIconFamily,
  TUIIconName,
  TUIIconProps,
  TUIIconSpec,
} from './types'
export { UI_ICON_NAMES } from './types'
export {
  FALLBACK_ICON_NAME,
  isValidIconName,
  toValidIconName,
} from './validate'

function resolveSize(size: number | string | undefined): number | string {
  if (size === undefined) return 'inherit'
  if (typeof size === 'string' && size in iconSize) {
    return iconSize[size as keyof typeof iconSize]
  }
  return size
}

/**
 * Renders a `UIIcon` by its Material Symbols product name.
 *
 * @example
 * <UIIcon name="edit" decorative />
 * <UIIcon name="delete" accessibleLabel="Delete item" />
 * <UIIcon name="star" fill size="lg" />
 */
export function UIIcon(props: TUIIconProps) {
  const { name, fill = false, size, color, className, sx } = props
  const accessibleLabel =
    'accessibleLabel' in props ? props.accessibleLabel : undefined

  const { glyph } = resolveGlyph(name, fill)

  return (
    <Box
      component="span"
      aria-hidden={accessibleLabel ? undefined : true}
      aria-label={accessibleLabel}
      role={accessibleLabel ? 'img' : undefined}
      className={className}
      sx={{
        fontFamily: iconFontFamily,
        fontFeatureSettings: "'liga'",
        fontWeight: 'normal',
        fontStyle: 'normal',
        fontSize: resolveSize(size),
        color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        lineHeight: 1,
        userSelect: 'none',
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {glyph}
    </Box>
  )
}
